// Receipt capture, ported from the press inspection audit app's photos page.
//
// The camera is reached with <input type="file" accept="image/*" capture>, which
// hands off to the phone's own camera app. That matters: the live-preview
// getUserMedia approach the Anvil app used only works in a secure context, so it
// silently fails on a phone pointed at a LAN address over plain http.
//
// Photos are resized before they ever leave the browser. A 4MB phone photo lands
// around 300KB, which is still perfectly legible for a receipt.
//
// Everything here is written to hold as little as possible and to let go of it
// the moment it is finished with. Entering receipts one after another used to
// kill the tab on Android with "Unable to complete previous operation due to low
// memory", because each capture:
//
//   * read the whole original into a base64 string (a 4MB photo -> ~5.3MB of JS
//     string) purely to hand it to an <img> as a data: URL,
//   * decoded that to a full-resolution bitmap (12MP -> ~48MB of pixels),
//   * drew it into a canvas, and then let go of none of it explicitly.
//
// Nothing there is a leak in the usual sense - it is all reachable garbage - but
// canvas and bitmap pixels live in native memory that the JS collector is in no
// hurry to reclaim, so doing it ten times in a row outruns the collector. The
// object URL, the ImageBitmap and the canvas are now all released by hand.

export const MAX_DIM = 1600
export const JPEG_QUALITY = 0.7

/** Anything larger than this is refused before we bother decoding it. */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024

/** What the server will accept. PDFs pass through untouched. */
export const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']

/**
 * Turns a picked file into a data URL ready to POST.
 *
 * Images are resized and re-encoded as JPEG. PDFs are read straight through,
 * since a vendor that emails a PDF receipt should not have to be photographed
 * off a screen.
 */
export async function fileToReceiptDataUrl(file) {
    if (!file) throw new Error('No file selected.')

    if (file.size > MAX_SOURCE_BYTES) {
        throw new Error(`That file is ${Math.round(file.size / 1024 / 1024)}MB, which is too large.`)
    }

    const type = (file.type || '').toLowerCase()
    if (!ACCEPTED_TYPES.includes(type)) {
        throw new Error('Receipts must be a photo (JPEG or PNG) or a PDF.')
    }

    // A PDF cannot be drawn to a canvas, and there is nothing to gain by
    // re-encoding it. Hand it over as it arrived.
    if (type === 'application/pdf') return readAsDataUrl(file)

    const source = await decode(file)
    try {
        return drawToDataUrl(source)
    } finally {
        release(source)
    }
}

/** Rough byte size of a data URL, for showing the user what they are about to send. */
export function dataUrlBytes(dataUrl) {
    if (!dataUrl) return 0
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
    return Math.round((base64.length * 3) / 4)
}

/* ---------------------------------------------------------------------- */

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(new Error('That file could not be read.'))
        reader.onload = () => resolve(reader.result)
        reader.readAsDataURL(file)
    })
}

/**
 * Decodes the file to something drawable, straight from the blob - never via a
 * base64 copy of it.
 *
 * createImageBitmap is preferred because it decodes off the main thread and,
 * unlike an <img>, can be closed the instant we are done with it.
 * `imageOrientation: 'from-image'` is not optional: without it a phone photo
 * taken sideways comes out rotated, which an <img> would have handled itself.
 */
async function decode(file) {
    if (typeof createImageBitmap === 'function') {
        try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' })
        } catch {
            // Older WebView, or a decoder that dislikes the file. Fall through
            // to the <img> path, which will report a genuinely broken image.
        }
    }
    return decodeViaImage(file)
}

function decodeViaImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()

        img.onload = () => {
            URL.revokeObjectURL(url)
            resolve(img)
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('That image could not be read.'))
        }

        img.src = url
    })
}

function drawToDataUrl(source) {
    const sourceWidth = source.width || source.naturalWidth
    const sourceHeight = source.height || source.naturalHeight
    const { width, height } = fit(sourceWidth, sourceHeight)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    canvas.getContext('2d').drawImage(source, 0, 0, width, height)

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)

    // Drops the canvas's pixel buffer now rather than whenever the collector
    // next runs. The element itself is unreferenced on return.
    canvas.width = 0
    canvas.height = 0

    return dataUrl
}

/** Longest side down to MAX_DIM, aspect kept. Smaller images are left alone. */
function fit(width, height) {
    if (width >= height && width > MAX_DIM) {
        return { width: MAX_DIM, height: Math.round((height * MAX_DIM) / width) }
    }
    if (height > width && height > MAX_DIM) {
        return { width: Math.round((width * MAX_DIM) / height), height: MAX_DIM }
    }
    return { width, height }
}

/** ImageBitmap frees on close; an <img> only once its src stops pointing at pixels. */
function release(source) {
    if (typeof source.close === 'function') source.close()
    else source.src = ''
}
